// TAMFARIS PWA install helper.
// - Register service worker (production HTTPS preview / prod).
// - Expose <InstallPWAButton /> — banner ringkas untuk memicu install:
//   • Chrome/Edge/Android: pakai event `beforeinstallprompt` (deferred prompt).
//   • iOS Safari (iPhone/iPad): tidak fire event → tampilkan instruksi
//     "Bagikan → Tambahkan ke Layar Utama".
// - Sembunyi otomatis bila app sudah ter-install (display-mode: standalone).

import React, { useEffect, useState, useCallback } from "react";
import { Download, Share, X } from "lucide-react";

export function registerPWA() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Register on load agar tidak mengganggu first paint.
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      // Silent — SW opsional; log ringan agar mudah didebug tanpa mengganggu user.
      // eslint-disable-next-line no-console
      console.warn("[PWA] SW register gagal:", err?.message || err);
    });
  });
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPad iPadOS 13+ tampil sbg Mac — deteksi tambahan touch.
  const iPadOS = ua.includes("Macintosh") && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

const DISMISS_KEY = "tamfaris_pwa_install_dismiss_v1";
function isDismissedRecently() {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!t) return false;
    // Sembunyi 7 hari setelah dismiss.
    return Date.now() - t < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}
function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export function InstallPWAButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(isDismissedRecently());
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (installed) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  const doInstall = useCallback(async () => {
    if (isIOS()) { setShowIOSHint(true); return; }
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice?.outcome === "dismissed") markDismissed();
    } catch { setDeferred(null); }
  }, [deferred]);

  const onDismiss = () => { markDismissed(); setDismissed(true); };

  if (installed) return null;
  if (dismissed && !showIOSHint) return null;

  // Tampilkan tombol jika:
  // - Ada deferred prompt (Chrome/Edge/Android), ATAU
  // - Ini iOS (belum ter-install) → tampilkan CTA yang membuka instruksi.
  const canInstall = !!deferred;
  const isIos = isIOS();
  if (!canInstall && !isIos) return null;

  return (
    <>
      <div
        role="dialog"
        aria-label="Pasang aplikasi TAMFARIS"
        data-testid="pwa-install-banner"
        className="pwa-install-pill"
      >
        <Download size={13} aria-hidden="true" className="pwa-install-pill-icon"/>
        <button
          type="button"
          onClick={doInstall}
          data-testid="pwa-install-btn"
          className="pwa-install-pill-cta"
        >
          {isIos ? "Pasang" : "Pasang TAMFARIS"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Tutup"
          data-testid="pwa-install-dismiss"
          className="pwa-install-pill-close"
        >
          <X size={12} />
        </button>
      </div>

      {showIOSHint && (
        <div
          role="dialog"
          aria-label="Cara memasang di iPhone"
          data-testid="pwa-ios-hint"
          onClick={() => setShowIOSHint(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            zIndex: 10000, display: "flex", alignItems: "flex-end", justifyContent: "center",
            padding: 12,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#111827", color: "#f8fafc", borderRadius: 14,
              padding: 16, maxWidth: 480, width: "100%",
              boxShadow: "0 -12px 40px rgba(0,0,0,0.5)",
              border: "1px solid #1f2937",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>Pasang di iPhone / iPad</div>
              <button
                type="button"
                onClick={() => setShowIOSHint(false)}
                aria-label="Tutup"
                data-testid="pwa-ios-hint-close"
                style={{ background: "transparent", border: "none", color: "#f8fafc", cursor: "pointer" }}
              >
                <X size={18} />
              </button>
            </div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
              <li>Ketuk ikon <b>Bagikan</b> <Share size={12} style={{ verticalAlign: "middle" }} /> di bawah Safari.</li>
              <li>Gulir dan pilih <b>Tambahkan ke Layar Utama</b>.</li>
              <li>Ketuk <b>Tambah</b> di kanan atas. Ikon TAMFARIS akan muncul di layar utama.</li>
            </ol>
            <div style={{ marginTop: 10, fontSize: 11, opacity: 0.75 }}>
              Catatan: fitur ini hanya tersedia di Safari (bukan Chrome iOS).
            </div>
            <button
              type="button"
              onClick={() => { setShowIOSHint(false); onDismiss(); }}
              data-testid="pwa-ios-hint-ok"
              style={{
                marginTop: 12, background: "#F97316", color: "#0e1015",
                border: "none", borderRadius: 8, padding: "10px 14px",
                fontWeight: 800, fontSize: 13, cursor: "pointer", width: "100%",
              }}
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </>
  );
}
